import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ChevronDown,
  CircleHelp,
  X,
} from "lucide-react";

const FAQS = [
  {
    question: "Why is my location not tracked while submitting attendance?",
    answer: "Turn on Location/GPS, allow this application to use your precise location, and make sure you have a stable internet connection. Then return to attendance and try again. If it still fails, restart the app or browser and contact your HR administrator with a screenshot and the time of the attempt.",
  },
  {
    question: "Where can I find my point of contact information?",
    answer: "Your reporting manager or HR point of contact is normally available in your employee profile or organisation chart. If no contact is shown, ask your HR administrator to update the reporting relationship for your employee record.",
  },
  {
    question: "How do I change my password?",
    answer: "Open your profile menu, go to Profile Settings, select Change Password, and enter your current and new passwords. Use a strong password that you do not use elsewhere. If you cannot sign in, use the Forgot Password option on the login page.",
  },
  {
    question: "How can I request services that are missing from my login?",
    answer: "Access to services is controlled by your assigned role. Send the service name and the reason you need access to your manager or HR administrator. After approval, an administrator can update your role and permissions.",
  },
  {
    question: "Why can't I see my latest attendance entry?",
    answer: "New entries can take a moment to appear. Refresh the attendance page and check that the selected date range includes today. If the entry is still missing, raise an attendance regularisation request or contact HR.",
  },
  {
    question: "How do I correct an incorrect attendance record?",
    answer: "Open Attendance, locate the affected date, and submit a regularisation request with the correct time and a short explanation. Your manager or HR team will review it according to your organisation's policy.",
  },
  {
    question: "How do I apply for leave and check its status?",
    answer: "Open Leave Management, choose Apply Leave, select the leave type and dates, and submit the request. You can follow approval progress from Leave History.",
  },
  {
    question: "What should I do if a page or feature is not loading?",
    answer: "Check your connection, refresh the page, and sign out and back in. If the issue continues, share the page name, time, browser or device, and a screenshot with your support contact.",
  },
];

export function FaqModal({ open, onOpenChange }) {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filteredFaqs = FAQS.map((faq, index) => ({ ...faq, index })).filter(
    (faq) => faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query)
  );

  useEffect(() => {
    if (!open) {
      setExpandedIndex(-1);
      setSearch("");
    }
  }, [open]);

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[#0a1230]/50 backdrop-blur-xs" />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-2xl focus:outline-hidden">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  <CircleHelp className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <DialogPrimitive.Title className="font-semibold text-(--color-text-primary)">Help &amp; FAQs</DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm per-file-hint">Quick answers to common questions</DialogPrimitive.Description>
                </div>
              </div>
              <DialogPrimitive.Close className="grid h-9 w-9 shrink-0 place-items-center rounded-lg per-file-hint hover:bg-workspace/60" aria-label="Close FAQs">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
              <section aria-labelledby="faq-heading">
                <h2 id="faq-heading" className="mb-3 text-sm font-semibold uppercase tracking-wide per-file-hint">Frequently asked questions</h2>
                <div className="mb-3">
                  <label htmlFor="faq-search" className="sr-only">Search FAQs</label>
                  <input data-page-search="true"
                    id="faq-search"
                    type="search"
                    value={search}
                    onChange={(event) => { setSearch(event.target.value); setExpandedIndex(-1); }}
                    placeholder="Search questions and answers..."
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-(--color-text-primary) outline-hidden focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                {filteredFaqs.length === 0 && <p role="status" className="py-4 text-sm per-file-hint">No FAQs match your search.</p>}
                <div className="space-y-2">
                  {filteredFaqs.map((faq) => {
                    const { index } = faq;
                    const expanded = expandedIndex === index;
                    const answerId = `faq-answer-${index}`;
                    return (
                      <div key={faq.question} className="overflow-hidden rounded-xl border border-border bg-white">
                        <button
                          type="button"
                          onClick={() => setExpandedIndex(expanded ? -1 : index)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-(--color-text-primary) hover:bg-workspace/40"
                          aria-expanded={expanded}
                          aria-controls={answerId}
                        >
                          <span>{faq.question}</span>
                          <ChevronDown className={`h-4 w-4 shrink-0 text-primary transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </button>
                        {expanded && <p id={answerId} className="border-t border-border bg-workspace/25 px-4 py-3 text-sm leading-6 per-file-hint">{faq.answer}</p>}
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
